import type { PhaseBuildExecutionState } from '../services/buildPhaseService';

export interface InsufficientCreditsPayload {
  balance?: number | null;
  required?: number | null;
  shortfall?: number | null;
}

const positiveFiniteNumber = (value: number | null | undefined): number | undefined => (
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined
);

/** Return the amount the user must add, not the total cost of the action. */
export const getCreditShortfall = (
  payload: InsufficientCreditsPayload | null | undefined,
): number | undefined => {
  const explicitShortfall = positiveFiniteNumber(payload?.shortfall);

  if (explicitShortfall !== undefined) return explicitShortfall;

  const required = positiveFiniteNumber(payload?.required);

  if (required === undefined) return undefined;

  const balance = typeof payload?.balance === 'number' && Number.isFinite(payload.balance)
    ? Math.max(0, payload.balance)
    : undefined;

  return balance === undefined ? required : positiveFiniteNumber(required - balance);
};

/**
 * Smallest top-up the purchase form preselects. Covering a 0.82-credit gap by
 * offering to buy 1 credit is a purchase the user would immediately have to
 * repeat, so the form starts here. It is a preselection, not a limit — the
 * amount field still accepts anything the user types.
 */
export const MINIMUM_TOP_UP_CREDITS = 100;

/** Credit purchases are whole-credit transactions, so round up a fractional gap. */
export const getTopUpCreditAmount = (
  payload: InsufficientCreditsPayload | null | undefined,
): number | undefined => {
  const shortfall = getCreditShortfall(payload);

  return shortfall === undefined
    ? undefined
    : Math.max(MINIMUM_TOP_UP_CREDITS, Math.ceil(shortfall));
};

/**
 * A build action is credit-blocked only when credits are its sole blocker.
 * Partial phase execution remains allowed; explicit task selection may still
 * produce its own shortfall even while the phase has affordable work.
 */
export const getPhaseActionCreditShortfall = (
  state: PhaseBuildExecutionState | null,
  selectedTaskShortfall = 0,
  selectionActive = false,
): number => {
  const selectionGap = positiveFiniteNumber(selectedTaskShortfall);

  // Once the user is choosing explicit tasks, that selection is the whole
  // build scope. Do not leak the phase-wide shortfall into an empty or
  // affordable selection; the picker should either ask for a task or run the
  // affordable selection normally.
  if (selectionActive) return selectionGap ?? 0;
  if (state?.blocking_reason !== 'insufficient_credits') return 0;

  return positiveFiniteNumber(state.credit_shortfall) ?? 0;
};
